import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { getSocket, isSocketConnected } from '../socket';
import './ProjectFilePreview.css';

const ThreeDViewer = lazy(() => import('./ThreeDViewer'));

const model3dExts = ['obj', 'fbx', 'gltf', 'glb', 'stl', '3ds', 'blend', 'ply'];
const textExts = ['txt', 'md', 'json', 'xml', 'csv', 'log', 'yml', 'yaml', 'toml', 'ini', 'cfg', 'conf', 'sh', 'bat', 'ps1', 'py', 'js', 'ts', 'jsx', 'tsx', 'css', 'scss', 'less', 'java', 'kt', 'cpp', 'c', 'h', 'cs', 'go', 'rs', 'rb', 'php', 'sql', 'r', 'swift', 'dart', 'lua', 'env', 'gitignore', 'dockerfile'];
const htmlExts = ['html', 'htm'];

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// Simple markdown to HTML (headings, bold, italic, code, links, lists)
const renderMarkdown = (text) => {
  let html = text
    // Code blocks
    .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    // Headings
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    // Bold & italic
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    // Unordered lists
    .replace(/^[*-] (.+)$/gm, '<li>$1</li>')
    // Line breaks (but not inside pre blocks)
    .replace(/\n/g, '<br>');
  // Wrap consecutive <li> in <ul>
  html = html.replace(/((?:<li>.*?<\/li><br>?)+)/g, '<ul>$1</ul>');
  html = html.replace(/<ul>(.*?)<\/ul>/gs, (match, inner) => '<ul>' + inner.replace(/<br>/g, '') + '</ul>');
  return html;
};

function ProjectFilePreview({ file, project, user, onClose }) {
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState(file.comments || []);
  const [show3D, setShow3D] = useState(false);
  const [textContent, setTextContent] = useState(null);
  const [textLoading, setTextLoading] = useState(false);
  const commentsEndRef = useRef(null);

  const ext = (file.fileName || '').split('.').pop().toLowerCase();
  const is3D = model3dExts.includes(ext);
  const isImage = file.category === 'image';
  const isVideo = file.category === 'video';
  const isPdf = ext === 'pdf';
  const isHtml = htmlExts.includes(ext);
  const isText = textExts.includes(ext);
  const isMd = ext === 'md';

  // Fetch text/code content for inline viewing
  useEffect(() => {
    if ((!isText && !isHtml) || !file.storageUrl) return;
    setTextLoading(true);
    fetch(file.storageUrl)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch');
        return res.text();
      })
      .then(text => { setTextContent(text); setTextLoading(false); })
      .catch(() => { setTextContent(null); setTextLoading(false); });
  }, [file.storageUrl, isText, isHtml]);

  // Keep comments in sync with file prop
  useEffect(() => {
    setComments(file.comments || []);
  }, [file.comments]);

  // Listen for real-time comment updates
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const handleAdded = ({ fileId, comment }) => {
      if (fileId === file.fileId) {
        setComments(prev => {
          if (prev.some(c => c.commentId === comment.commentId)) return prev;
          return [...prev, comment];
        });
      }
    };
    const handleDeleted = ({ fileId, commentId }) => {
      if (fileId === file.fileId) {
        setComments(prev => prev.filter(c => c.commentId !== commentId));
      }
    };

    socket.on('project:file:comment:added', handleAdded);
    socket.on('project:file:comment:deleted', handleDeleted);
    return () => {
      socket.off('project:file:comment:added', handleAdded);
      socket.off('project:file:comment:deleted', handleDeleted);
    };
  }, [file.fileId]);

  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments]);

  const handleAddComment = () => {
    if (!commentText.trim()) return;
    const socket = getSocket();
    if (socket && isSocketConnected()) {
      socket.emit('project:file:comment:add', {
        projectId: project.projectId,
        fileId: file.fileId,
        content: commentText.trim()
      });
    }
    setCommentText('');
  };

  const handleDeleteComment = (commentId) => {
    const socket = getSocket();
    if (socket && isSocketConnected()) {
      socket.emit('project:file:comment:delete', {
        projectId: project.projectId,
        fileId: file.fileId,
        commentId
      });
    }
  };

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        if (show3D) setShow3D(false);
        else onClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose, show3D]);

  if (show3D && is3D) {
    return (
      <Suspense fallback={
        <div className="pfp-overlay" onClick={onClose}>
          <div className="pfp-loading">Loading 3D Viewer...</div>
        </div>
      }>
        <ThreeDViewer
          modelUrl={file.storageUrl}
          fileName={file.fileName}
          onClose={() => setShow3D(false)}
        />
      </Suspense>
    );
  }

  // Render the preview content based on file type
  const renderPreview = () => {
    // Images
    if (isImage && file.storageUrl) {
      return <img src={file.storageUrl} alt={file.fileName} className="pfp-image" />;
    }

    // Videos
    if (isVideo && file.storageUrl) {
      return <video src={file.storageUrl} controls className="pfp-video" />;
    }

    // 3D models — show prompt to open full viewer
    if (is3D) {
      return (
        <div className="pfp-3d-prompt">
          <span className="material-symbols-outlined pfp-3d-icon">view_in_ar</span>
          <p>{file.fileName}</p>
          <button className="pfp-3d-btn" onClick={() => setShow3D(true)}>
            Open 3D Viewer
          </button>
        </div>
      );
    }

    // PDF — embedded viewer
    if (isPdf && file.storageUrl) {
      return (
        <iframe
          src={file.storageUrl}
          className="pfp-iframe"
          title={file.fileName}
        />
      );
    }

    // HTML — sandboxed iframe with fetched content
    if (isHtml && file.storageUrl) {
      if (textLoading) {
        return <div className="pfp-text-loading"><span className="material-symbols-outlined">hourglass_top</span> Loading...</div>;
      }
      if (textContent !== null) {
        const blob = new Blob([textContent], { type: 'text/html' });
        const blobUrl = URL.createObjectURL(blob);
        return (
          <iframe
            src={blobUrl}
            className="pfp-iframe"
            title={file.fileName}
            sandbox="allow-same-origin"
            onLoad={() => URL.revokeObjectURL(blobUrl)}
          />
        );
      }
      // Fallback: open in new tab
      return (
        <div className="pfp-doc-prompt">
          <span className="material-symbols-outlined pfp-doc-icon" style={{ color: '#FF5722' }}>code</span>
          <p>{file.fileName}</p>
          <a href={file.storageUrl} target="_blank" rel="noopener noreferrer" className="pfp-download-btn">Open in Browser</a>
        </div>
      );
    }

    // Markdown — rendered
    if (isMd && textContent !== null) {
      if (textLoading) {
        return <div className="pfp-text-loading"><span className="material-symbols-outlined">hourglass_top</span> Loading...</div>;
      }
      return (
        <div className="pfp-markdown" dangerouslySetInnerHTML={{ __html: renderMarkdown(textContent) }} />
      );
    }

    // Text / code files — syntax-highlighted-ish code view
    if (isText && file.storageUrl) {
      if (textLoading) {
        return <div className="pfp-text-loading"><span className="material-symbols-outlined">hourglass_top</span> Loading...</div>;
      }
      if (textContent !== null) {
        const lines = textContent.split('\n');
        return (
          <div className="pfp-code-viewer">
            <div className="pfp-code-header">
              <span className="material-symbols-outlined">code</span>
              {file.fileName}
              <span className="pfp-code-lines">{lines.length} lines</span>
            </div>
            <pre className="pfp-code-content">
              <table className="pfp-code-table">
                <tbody>
                  {lines.map((line, i) => (
                    <tr key={i}>
                      <td className="pfp-line-num">{i + 1}</td>
                      <td className="pfp-line-text">{line}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </pre>
          </div>
        );
      }
    }

    // Fallback — download prompt
    return (
      <div className="pfp-doc-prompt">
        <span className="material-symbols-outlined pfp-doc-icon">insert_drive_file</span>
        <p>{file.fileName}</p>
        <a href={file.storageUrl} target="_blank" rel="noopener noreferrer" className="pfp-download-btn" download>
          Download File
        </a>
      </div>
    );
  };

  return (
    <div className="pfp-overlay" onClick={onClose}>
      <div className="pfp-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="pfp-close" onClick={onClose}>
          <span className="material-symbols-outlined">close</span>
        </button>

        {/* Preview area */}
        <div className="pfp-preview">
          {renderPreview()}
        </div>

        {/* File info bar */}
        <div className="pfp-info-bar">
          <span className="pfp-info-name">{file.fileName}</span>
          <span className="pfp-info-meta">
            {formatFileSize(file.fileSize)}
            {file.uploadedByName ? ` · ${file.uploadedByName}` : ''}
            {file.uploadedAt ? ` · ${new Date(file.uploadedAt).toLocaleDateString()}` : ''}
          </span>
          <a href={file.storageUrl} target="_blank" rel="noopener noreferrer" className="pfp-info-download" download>
            <span className="material-symbols-outlined">download</span>
          </a>
        </div>

        {/* Comments section */}
        <div className="pfp-comments">
          <div className="pfp-comments-header">
            <span className="material-symbols-outlined">comment</span>
            Comments ({comments.length})
          </div>
          <div className="pfp-comments-list">
            {comments.length === 0 && (
              <div className="pfp-no-comments">No comments yet</div>
            )}
            {comments.map(c => (
              <div key={c.commentId} className="pfp-comment">
                <div className="pfp-comment-header">
                  <span className="pfp-comment-user">{c.userName}</span>
                  <span className="pfp-comment-time">
                    {new Date(c.createdAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                  {c.userId === user.uid && (
                    <button className="pfp-comment-delete" onClick={() => handleDeleteComment(c.commentId)}>
                      <span className="material-symbols-outlined">close</span>
                    </button>
                  )}
                </div>
                <div className="pfp-comment-text">{c.content}</div>
              </div>
            ))}
            <div ref={commentsEndRef} />
          </div>
          <div className="pfp-comment-input">
            <input
              type="text"
              placeholder="Add a comment..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
            />
            <button onClick={handleAddComment} disabled={!commentText.trim()}>
              <span className="material-symbols-outlined">send</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProjectFilePreview;
